import {
  useTranslation,
  type TranslationKeys,
} from "@/hooks/useTranslation/useTranslation"
import { cn } from "@/lib/utils"
import type {
  DraggableAttributes,
  DraggableSyntheticListeners,
} from "@dnd-kit/core"
import { ArrowRight, GripVertical } from "lucide-react"
import { Link } from "react-router"

export interface WidgetCardDragHandleProps {
  attributes: DraggableAttributes
  listeners: DraggableSyntheticListeners
  setActivatorNodeRef: (element: HTMLElement | null) => void
}

export interface WidgetCardProps {
  titleKey: TranslationKeys
  countText?: string
  linkTo?: string
  linkLabelKey?: TranslationKeys
  wide?: boolean
  className?: string
  headerExtra?: React.ReactNode
  editing?: boolean
  dragHandleProps?: WidgetCardDragHandleProps
  children: React.ReactNode
}

export function WidgetCard({
  titleKey,
  countText,
  linkTo,
  linkLabelKey,
  wide,
  className,
  headerExtra,
  editing,
  dragHandleProps,
  children,
}: WidgetCardProps) {
  const { translate } = useTranslation()
  return (
    <section
      className={cn(
        "min-w-0 rounded-xl border border-grey4 bg-grey2 px-[26px] py-6 transition-all duration-200 ease-out",
        wide && "col-span-full",
        className,
      )}
    >
      <div className="mb-4 flex items-center gap-3">
        {editing && dragHandleProps && (
          <button
            type="button"
            ref={dragHandleProps.setActivatorNodeRef}
            aria-label={`${translate("MC_SSD_DRAG_HANDLE")}: ${translate(titleKey)}`}
            className="animate-in fade-in shrink-0 cursor-grab rounded-sm text-grey5 duration-200 hover:text-white focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring active:cursor-grabbing"
            {...dragHandleProps.attributes}
            {...dragHandleProps.listeners}
          >
            <GripVertical size={15} aria-hidden="true" />
          </button>
        )}
        <span className="text-[14px] font-semibold text-white">
          {translate(titleKey)}
        </span>
        {countText && (
          <span className="font-mono text-xs text-grey5">{countText}</span>
        )}
        <span className="ml-auto flex items-center gap-2">
          {headerExtra}
          {linkTo && linkLabelKey && (
            <Link
              to={linkTo}
              className="inline-flex items-center gap-1 text-xs font-medium text-grey5 transition-colors hover:text-white"
            >
              {translate(linkLabelKey)}
              <ArrowRight size={13} />
            </Link>
          )}
        </span>
      </div>
      {children}
    </section>
  )
}
